import type { Contexto } from '../app/contexto';
import { consultaMovil } from '../app/movil';
import type { Bbox, Punto, MultiPoligono } from '../geo/geometria';
import { bboxMultiPoligono } from '../geo/geometria';
import { multiPoligonoDeGeoJson } from '../geo/recorte';
import type { GeometriasUtm } from '../export/exportarDxf';
import type { Ficha } from '../db/consultas';
import type { Tesela } from '../geo/teselado';
import { descargarGml, urlParcelaPorRc } from '../catastro/wfs';
import { parsearParcelas } from '../catastro/gml';
import {
  crearArea, esperarArea, estadoArea, areaGuardada, guardarArea, claveModelo, modeloGuardado, guardarModelo,
  cargarModeloInfo, cargarPlanta, type EstadoArea, type HuellaPeticion, type ModeloInfo, type Planta,
} from './api';

/**
 * Prepara el LiDAR del entorno de una parcela: pide al backend un area (radio alrededor de la
 * parcela) con las huellas de los edificios cargados dentro, espera a que este lista y aplica
 * las alturas medidas a la base local. Si ya se preparo antes en este navegador, reutiliza el
 * area guardada.
 */
export const RADIO_LIDAR_M = 150;

export interface OpcionesPreparar {
  radioM?: number;
  onEstado?: (e: EstadoArea) => void;
  senal?: AbortSignal;
  forzar?: boolean;
}

export async function prepararLidar(ctx: Contexto, rc: string, op: OpcionesPreparar = {}): Promise<EstadoArea> {
  const radio = op.radioM ?? RADIO_LIDAR_M;
  const f = await ctx.db.ficha(rc);
  if (!f) throw new Error('La parcela no está en la base local; carga la zona primero.');
  const p = f.parcela;
  const bbox: Bbox = [p.minx - radio, p.miny - radio, p.maxx + radio, p.maxy + radio];

  let estado: EstadoArea | null = null;
  const previa = op.forzar ? null : areaGuardada(rc);
  if (previa) {
    try {
      estado = await estadoArea(previa);
      if (estado.estado === 'error') estado = null;
    } catch {
      estado = null;
    }
  }
  if (!estado) {
    const g = await ctx.db.geometriasUtm(bbox);
    const huellas: HuellaPeticion[] = g.edificios.slice(0, 200).map((e) => ({ rc: e.rc, geom: e.geom }));
    // La parcela elegida va la primera para que el backend la destaque en el glTF.
    huellas.sort((a, b) => (a.rc === rc ? -1 : b.rc === rc ? 1 : 0));
    estado = await crearArea(bbox, p.epsg, rc, huellas);
    guardarArea(rc, estado.id);
  }
  op.onEstado?.(estado);
  if (estado.estado !== 'listo') estado = await esperarArea(estado.id, (e) => op.onEstado?.(e), op.senal);
  if (estado.estado === 'error') throw new Error(estado.error ?? 'El backend devolvió un error');

  await aplicarAlturas(ctx, estado);
  return estado;
}

// ---------- Modelo LiDAR recortado (parcela o rectangulo de zona) ----------

/** Margen maximo que calcula el servidor alrededor de una parcela (tope del deslizador "Desfase"). */
export const MARGEN_MODELO_M = 150;
/** En movil y tableta el modelo se pide con 50 m, como antes: con 150 tarda 4 veces mas y pesa unos 28 MB. */
export const MARGEN_MODELO_MOVIL_M = 50;

/** Margen con el que se pide el modelo de una parcela en este dispositivo. */
export function margenModelo(): number {
  return consultaMovil()?.matches ? MARGEN_MODELO_MOVIL_M : MARGEN_MODELO_M;
}
/**
 * Margen de los rectangulos de zona: se queda en 50 m porque cuenta en los 4 km2 del modelo y en las 4
 * hojas LiDAR de 1 km que admite el servidor.
 */
export const MARGEN_ZONA_M = 50;
/**
 * Limites del contrato: huellas de edificio y partes (Catastro 3D) por peticion. Con 150 m de margen
 * el area es unas 7 veces la de 50 m y en ciudad los 200/600 de antes dejaban sin huella el anillo exterior.
 */
export const MAX_HUELLAS_EDIFICIO = 600;
export const MAX_HUELLAS_PARTE = 1800;
/** En los rectangulos de zona (hasta 4 km2 y 50 m de margen) siguen los topes de antes: medir y tejar mas huellas triplicaba el tiempo. */
export const MAX_HUELLAS_EDIFICIO_ZONA = 200;
export const MAX_HUELLAS_PARTE_ZONA = 600;
/** Un rectangulo de zona con su margen no puede pasar de esto (el servidor ejecuta todo el proceso). */
export const MAX_KM2_MODELO = 4;

export type ObjetivoModelo =
  | { tipo: 'parcela'; rc: string; geom: string; epsg: number }
  | { tipo: 'zona'; bbox: Bbox; epsg: number };

export interface OpcionesModeloPreparar {
  margenM?: number;
  intervaloCurvas?: number;
  onEstado?: (e: EstadoArea) => void;
  senal?: AbortSignal;
  forzar?: boolean;
}

/** Poligono de recorte del objetivo como GeoJSON (la parcela tal cual; la zona como rectangulo). */
export function poligonoObjetivo(o: ObjetivoModelo): string {
  if (o.tipo === 'parcela') return o.geom;
  const [x0, y0, x1, y1] = o.bbox;
  return JSON.stringify({ type: 'Polygon', coordinates: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]]] });
}

export function claveObjetivo(o: ObjetivoModelo): string {
  return o.tipo === 'parcela' ? claveModelo({ rc: o.rc }) : claveModelo({ bbox: o.bbox, epsg: o.epsg });
}

/** Caja del poligono ampliada con el margen (para buscar hojas y huellas). */
export function bboxObjetivo(o: ObjetivoModelo, margen: number): Bbox {
  const b = o.tipo === 'zona' ? o.bbox : bboxMultiPoligono(multiPoligonoDeGeoJson(o.geom));
  return [b[0] - margen, b[1] - margen, b[2] + margen, b[3] + margen];
}

function centroCaja(geom: string): Punto | null {
  const mp = multiPoligonoDeGeoJson(geom);
  if (!mp.length) return null;
  const b = bboxMultiPoligono(mp);
  return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
}

/**
 * Huellas para el registro de Walls/Roof: edificios (tipo 'edificio') y partes del Catastro con
 * sus plantas (tipo 'parte', alturaM = plantasSobre x 3, que hacen de "Catastro 3D"). Si hay mas
 * de `max` (por defecto, las que admite el contrato), se quedan las mas cercanas al centro del objetivo.
 */
export function huellasModelo(g: GeometriasUtm, centro: Punto, rcDestacada: string | null,
  max: { edificios: number; partes: number } = { edificios: MAX_HUELLAS_EDIFICIO, partes: MAX_HUELLAS_PARTE }): HuellaPeticion[] {
  const cerca = <T extends { geom: string }>(lista: T[], max: number): T[] => {
    if (lista.length <= max) return lista;
    const d = (x: T) => { const c = centroCaja(x.geom); return c ? Math.hypot(c[0] - centro[0], c[1] - centro[1]) : Infinity; };
    return lista.map((x) => ({ x, d: d(x) })).sort((a, b) => a.d - b.d).slice(0, max).map((v) => v.x);
  };
  const edificios: HuellaPeticion[] = cerca(g.edificios, max.edificios).map((e) => ({ rc: e.rc, geom: e.geom, tipo: 'edificio' }));
  // Las partes sin plantas sobre rasante (patios, sotanos) no son volumen: no sirven de Catastro 3D.
  const partes: HuellaPeticion[] = cerca(g.partes.filter((x) => (x.plantasSobre ?? 0) > 0), max.partes)
    .map((x) => ({ rc: x.rc, geom: x.geom, tipo: 'parte', alturaM: (x.plantasSobre ?? 0) * 3 }));
  // La parcela elegida va la primera para que el backend la destaque.
  if (rcDestacada) edificios.sort((a, b) => (a.rc === rcDestacada ? -1 : b.rc === rcDestacada ? 1 : 0));
  return [...edificios, ...partes];
}

/**
 * Prepara el modelo LiDAR recortado (contrato_modelo): cubiertas, muros con la huella corregida,
 * vegetacion, MDT 50 cm y curvas alrededor del poligono de una parcela o de un rectangulo de zona,
 * hasta `margenM`. Reutiliza el area guardada en este navegador si sigue viva, y aplica las
 * alturas medidas a la base local.
 */
export async function prepararModelo(ctx: Contexto, objetivo: ObjetivoModelo, op: OpcionesModeloPreparar = {}): Promise<EstadoArea> {
  const margen = Math.max(0, Math.min(MARGEN_MODELO_M, op.margenM ?? margenModelo()));
  const bbox = bboxObjetivo(objetivo, margen);
  const clave = claveObjetivo(objetivo);
  const rc = objetivo.tipo === 'parcela' ? objetivo.rc : null;

  let estado: EstadoArea | null = null;
  const previa = op.forzar ? null : modeloGuardado(clave);
  if (previa) {
    try {
      estado = await estadoArea(previa);
      // La clave local no lleva el margen: un area calculada con menos (p. ej. 50 m, antes de subirlo) se pide de nuevo.
      if (estado.estado === 'error' || !cubreMargen(estado, margen)) estado = null;
    } catch {
      estado = null;
    }
  }
  if (!estado) {
    // Las partes (plantas por cuerpo) solo estan si se cargaron o se vio el 3D: se piden las que falten.
    await ctx.db.cargarPartes(bbox, objetivo.epsg, 7).catch(() => undefined);
    const g = await ctx.db.geometriasUtm(bbox);
    const centro: Punto = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    const huellas = huellasModelo(g, centro, rc, objetivo.tipo === 'zona'
      ? { edificios: MAX_HUELLAS_EDIFICIO_ZONA, partes: MAX_HUELLAS_PARTE_ZONA } : undefined);
    estado = await crearArea(bbox, objetivo.epsg, rc, huellas, {
      modelo: true,
      poligono: poligonoObjetivo(objetivo),
      margenM: margen,
      intervaloCurvas: op.intervaloCurvas ?? 1,
    });
    guardarModelo(clave, estado.id);
  }
  op.onEstado?.(estado);
  if (estado.estado !== 'listo') estado = await esperarArea(estado.id, (e) => op.onEstado?.(e), op.senal);
  if (estado.estado === 'error') {
    // Si el nuevo (p. ej. el de 150 m) falla, la ficha, el informe y el DXF siguen con el que habia.
    if (previa && previa !== estado.id) guardarModelo(clave, previa);
    throw new Error(estado.error ?? 'El backend devolvió un error');
  }
  await aplicarAlturas(ctx, estado);
  return estado;
}

/** El area se calculo con al menos `margen` m alrededor del poligono (las de antes del campo, con 50). */
export function cubreMargen(estado: Pick<EstadoArea, 'margenM'>, margen: number): boolean {
  return (estado.margenM ?? 50) >= margen - 0.01;
}

/** Alturas medidas del area (alero, cumbrera, huella corregida) a la base local. */
export async function aplicarAlturas(ctx: Contexto, estado: EstadoArea): Promise<void> {
  const medidas = (estado.alturas ?? []).filter((a) => a.cumbreraM !== null);
  if (medidas.length) {
    await ctx.db.aplicarAlturas(medidas.map((a) => ({ rc: a.rc, aleroM: a.aleroM, cumbreraM: a.cumbreraM, huellaCorregida: a.huellaCorregida, desviacionM: a.desviacionM })));
  }
}

/** Margen de Catastro que se descarga alrededor de la parcela: el del modelo y un poco mas para las huellas que lo cruzan. */
export const MARGEN_CATASTRO_PARCELA_M = MARGEN_MODELO_M + 10;

/** Caja (redondeada al metro) del Catastro que necesita el modelo de una parcela. */
export function teselaEntornoParcela(bbox: Bbox, epsg: number, margen = MARGEN_CATASTRO_PARCELA_M): Tesela {
  const minx = Math.floor(bbox[0] - margen), miny = Math.floor(bbox[1] - margen);
  const maxx = Math.ceil(bbox[2] + margen), maxy = Math.ceil(bbox[3] + margen);
  return { id: `m_${epsg}_${minx}_${miny}_${maxx}_${maxy}`, epsg, minx, miny, maxx, maxy };
}

/**
 * Deja en la base local la parcela y su entorno (parcelas, edificios y partes del Catastro hasta
 * MARGEN_CATASTRO_PARCELA_M), sin cargar la zona: es lo que necesita el modelo LiDAR. Si la parcela
 * no esta, se pide primero al Catastro por referencia para conocer su caja. La descarga queda
 * registrada como una tesela propia y se reutiliza mientras este vigente.
 */
export async function asegurarEntornoParcela(ctx: Contexto, rc: string, epsgPreferido: number,
  onMensaje?: (m: string) => void): Promise<Ficha> {
  let f = await ctx.db.ficha(rc);
  let bbox: Bbox, epsg: number;
  if (f) {
    bbox = [f.parcela.minx, f.parcela.miny, f.parcela.maxx, f.parcela.maxy];
    epsg = f.parcela.epsg;
  } else {
    onMensaje?.('Pidiendo la parcela al Catastro…');
    const { registros, epsg: epsgGml } = parsearParcelas(await descargarGml(urlParcelaPorRc(rc, epsgPreferido)));
    const p = registros.find((r) => r.geom.length > 0);
    if (!p) throw new Error(`El Catastro no devolvió la parcela ${rc}.`);
    bbox = bboxMultiPoligono(p.geom);
    epsg = epsgGml ?? epsgPreferido;
  }
  onMensaje?.('Descargando el Catastro de la parcela y su entorno…');
  const r = await ctx.db.cargarZona([teselaEntornoParcela(bbox, epsg)], ['cp', 'bu', 'bp'], 7, 3,
    (p) => onMensaje?.(`Descargando el Catastro de la parcela y su entorno (${p.hechas}/${p.total})…`));
  f = await ctx.db.ficha(rc);
  if (!f) throw new Error(r.errores[0] ?? `La parcela ${rc} no se pudo cargar del Catastro.`);
  return f;
}

/**
 * Modelo de un enlace compartido (`a` en la direccion): el area del servidor si sigue alli, esta lista,
 * tiene modelo y es de esa parcela; si no, null (y el modelo se calcula de nuevo).
 */
export async function areaCompartida(id: string, rc: string): Promise<EstadoArea | null> {
  try {
    const e = await estadoArea(id);
    return e.estado === 'listo' && e.productos['modelo.json'] && (!e.rc || e.rc === rc) ? e : null;
  } catch {
    return null;
  }
}

/** Modelo ya preparado en este navegador y listo en el servidor (sin crear nada), o null. */
export async function modeloListo(clave: string): Promise<EstadoArea | null> {
  const id = modeloGuardado(clave);
  if (!id) return null;
  try {
    const e = await estadoArea(id);
    return e.estado === 'listo' && e.productos['modelo.json'] && e.productos['planta.json'] ? e : null;
  } catch {
    return null;
  }
}

export interface PlantaDeModelo {
  estado: EstadoArea;
  info: ModeloInfo;
  planta: Planta;
  poligono: MultiPoligono;
}

/** modelo.json + planta.json de un modelo listo (para el DXF y el PDF), o null si no hay. */
export async function plantaDeModelo(clave: string): Promise<PlantaDeModelo | null> {
  const estado = await modeloListo(clave);
  if (!estado) return null;
  const [info, planta] = await Promise.all([cargarModeloInfo(estado.productos['modelo.json']), cargarPlanta(estado.productos['planta.json'])]);
  return { estado, info, planta, poligono: multiPoligonoDeGeoJson(info.poligono) };
}

/**
 * Area LiDAR sin modelo alrededor de un punto (+-radio): la nube completa de las hojas del IGN
 * para el modo 3D del mapa. Se recuerda por el punto redondeado a 50 m.
 */
export async function prepararLidarPunto(ctx: Contexto, x: number, y: number, epsg: number, op: OpcionesPreparar = {}): Promise<EstadoArea> {
  const radio = op.radioM ?? RADIO_LIDAR_M;
  const bbox: Bbox = [x - radio, y - radio, x + radio, y + radio];
  const clave = `punto:${epsg}_${Math.round(x / 50) * 50}_${Math.round(y / 50) * 50}_${radio}`;
  let estado: EstadoArea | null = null;
  const previa = op.forzar ? null : areaGuardada(clave);
  if (previa) {
    try {
      estado = await estadoArea(previa);
      if (estado.estado === 'error') estado = null;
    } catch {
      estado = null;
    }
  }
  if (!estado) {
    const g = await ctx.db.geometriasUtm(bbox).catch(() => null);
    // La nube completa del mapa 3D no tiene modelo: con los topes de antes (subirlos triplicaba las mediciones).
    const huellas: HuellaPeticion[] = (g?.edificios ?? []).slice(0, MAX_HUELLAS_EDIFICIO_ZONA).map((e) => ({ rc: e.rc, geom: e.geom }));
    estado = await crearArea(bbox, epsg, null, huellas);
    guardarArea(clave, estado.id);
  }
  op.onEstado?.(estado);
  if (estado.estado !== 'listo') estado = await esperarArea(estado.id, (e) => op.onEstado?.(e), op.senal);
  if (estado.estado === 'error') throw new Error(estado.error ?? 'El backend devolvió un error');
  await aplicarAlturas(ctx, estado);
  return estado;
}
